import React, {useCallback, useEffect, useState} from 'react';
import {Image} from 'react-bootstrap';
import {useAppSelector, useAppDispatch} from 'store/customer';
import styled, {useTheme} from 'styled-components';
import {
    PreviewPosition,
    PreviewLocation,
} from 'components/customer/Preview3D/types';
import {
    PerspectiveRight,
    DrawerClosed,
    DrawerOpen,
    IsometricLeft,
    IsometricRight,
    PerspectiveFront,
    PerspectiveLeft,
    Solid,
    Wireframe,
    Expand,
    Collapse,
} from 'assets';
import {
    getIsPreviewHighlighted,
    setIsPreviewHighlighted,
    setPreviewHighlightUpdateFreeze,
    getShowExpandedView,
    setShowExpandedView,
} from 'components/customer/Preview3DCommon/store/viewerSlice';
import ExpandedView from 'components/customer/Preview3D/components/ExpandedView';
import {debounce} from 'lodash';
import {getColoredImage} from 'shared/helpers';
import TWEEN from '@tweenjs/tween.js';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {useSurveyButton} from 'shared/components/Surveys/helpers/useSurveyButton';
import {SURVEYS} from 'shared/components/Surveys/helpers/useSurveys';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import {isMobile, isTablet} from 'react-device-detect';

interface PreviewControlProps {
    previewPosition: PreviewPosition;
    setPreviewPosition: React.Dispatch<React.SetStateAction<PreviewPosition>>;
    onPreviewPositionChanged: (previewPosition: PreviewPosition) => void;
    toggleLabel: string;
    onTogglePreview: React.MouseEventHandler<HTMLImageElement>;
    showToggle: boolean;
    showTexture: boolean;
    isDoorOpen: boolean;
    previewLocation: PreviewLocation;
    expanded?: boolean;
    hideExpandIcon?: boolean;
    handleClick: () => void;
    isStandalonePreview?: boolean;
    setShowTexture?: React.Dispatch<React.SetStateAction<boolean>>;
    updateMaterials?: (textureValue: boolean) => void;
}

interface ViewOptionProps {
    direction?: 'horizontal' | 'vertical'; // Optional prop with allowed values
    $isMiniBrowser: boolean;
}

type Option = {
    title: string;
    imgSrc: string;
    selected: boolean;
    defaultColor: string;
    clickHandler: React.MouseEventHandler<HTMLImageElement>;
};
const PreviewControl = ({
    previewPosition,
    setPreviewPosition,
    onPreviewPositionChanged,
    toggleLabel,
    onTogglePreview,
    showToggle,
    showTexture,
    isDoorOpen,
    previewLocation,
    expanded,
    hideExpandIcon,
    handleClick,
    isStandalonePreview,
    setShowTexture,
    updateMaterials,
}: PreviewControlProps) => {
    const {isMiniBrowser} = use3DContext();
    const dispatch = useAppDispatch();
    const isPreviewHighlighted = useAppSelector(getIsPreviewHighlighted);
    const showExpandedView = useAppSelector(getShowExpandedView);
    const theme = useTheme();
    const {showSurveyForm} = useSurveyButton(SURVEYS.ProductVisualizer3d, true);

    const primaryColour = theme.colors.primary.main;
    const secondaryColour = theme.colors.secondary.main;

    const [isAnimating, setIsAnimating] = useState(false);

    const handlePreviewHighlightUpdateFreeze = debounce(() => {
        dispatch(setPreviewHighlightUpdateFreeze(false));
    }, 1000);

    const changePreviewPosition = (position: PreviewPosition) => {
        showSurveyForm();
        setPreviewPosition(position);
        localStorage.setItem('PREVIEW_POSITION', position);
        onPreviewPositionChanged(position);

        dispatch(setIsPreviewHighlighted(true));
        dispatch(setPreviewHighlightUpdateFreeze(true));

        handlePreviewHighlightUpdateFreeze();
    };

    const setShowExpandedViewCallback = useCallback(
        (show: boolean) => {
            dispatch(setShowExpandedView({type: previewLocation, value: show}));
        },
        [previewLocation]
    );

    const expandedViewHandler = useCallback(() => {
        setShowTexture?.(true);
        updateMaterials?.(true);
        showSurveyForm();
        dispatch(
            setShowExpandedView({
                type: previewLocation,
                value: !showExpandedView[`${previewLocation}`],
            })
        );
    }, [showExpandedView, previewLocation, showSurveyForm]);

    const changePreviewPositionToLeft = useCallback(() => {
        changePreviewPosition('LEFT');
    }, [showSurveyForm]);
    const changePreviewPositionToRight = useCallback(() => {
        changePreviewPosition('RIGHT');
    }, [showSurveyForm]);
    const changePreviewPositionToLeftLeveled = useCallback(() => {
        changePreviewPosition('LEFT_LEVELED');
    }, [showSurveyForm]);
    const changePreviewPositionToFront = useCallback(() => {
        changePreviewPosition('FRONT');
    }, [showSurveyForm]);
    const changePreviewPositionToRightLeveled = useCallback(() => {
        changePreviewPosition('RIGHT_LEVELED');
    }, [showSurveyForm]);

    const togglePreviewWithSurvey = useCallback<
        React.MouseEventHandler<HTMLImageElement>
    >(
        (event) => {
            showSurveyForm();
            onTogglePreview(event);
        },
        [onTogglePreview, showSurveyForm]
    );

    useEffect(() => {
        // Check every 100ms if there are active tweens
        const interval = setInterval(() => {
            // Animations are now added to the global TWEEN group
            const activeAnimations =
                TWEEN.getAll().filter((t) => t.isPlaying()).length > 0;
            setIsAnimating(activeAnimations);
        }, 100);

        // Cleanup the interval on component unmount
        return () => clearInterval(interval);
    }, []);

    const options: Option[] = [
        {
            title: 'Left View',
            imgSrc: PerspectiveLeft,
            selected: previewPosition === 'LEFT_LEVELED',
            defaultColor: '#1591cc',
            clickHandler: changePreviewPositionToLeftLeveled,
        },
        {
            title: 'Right View',
            imgSrc: PerspectiveRight,
            selected: previewPosition === 'RIGHT_LEVELED',
            defaultColor: '#1591cc',
            clickHandler: changePreviewPositionToRightLeveled,
        },
        {
            title: 'Front View',
            imgSrc: PerspectiveFront,
            selected: previewPosition === 'FRONT',
            defaultColor: '#1392cd',
            clickHandler: changePreviewPositionToFront,
        },

        {
            title: 'Top Left View',
            imgSrc: IsometricLeft,
            selected: previewPosition === 'LEFT',
            defaultColor: '#1591cc',
            clickHandler: changePreviewPositionToLeft,
        },
        {
            title: 'Top Right View',
            imgSrc: IsometricRight,
            selected: previewPosition === 'RIGHT',
            defaultColor: '#1591cc',
            clickHandler: changePreviewPositionToRight,
        },
    ];

    const isSmallerDevice = isTablet || isMobile;

    const hasDivider = isStandalonePreview && !isSmallerDevice;

    return (
        <ViewOptionContainer
            $isMiniBrowser={!!isMiniBrowser}
            {...(isStandalonePreview
                ? {direction: isSmallerDevice ? 'horizontal' : 'vertical'}
                : null)}>
            {showToggle ? (
                <OverlayTrigger
                    placement="bottom"
                    overlay={<strong>{toggleLabel}</strong>}>
                    <StyledImageAction
                        className="icon_button"
                        $expanded={expanded}
                        style={{
                            backgroundColor: 'transparent',
                            pointerEvents: isAnimating ? 'none' : undefined,
                        }}
                        src={
                            getColoredImage(
                                isDoorOpen ? DrawerClosed : DrawerOpen,
                                '#204480',
                                primaryColour
                            ) as string
                        }
                        onClick={handleClick}
                    />
                </OverlayTrigger>
            ) : null}

            <OverlayTrigger
                placement="bottom"
                overlay={
                    <strong>
                        {showTexture
                            ? 'Switch to Wireframe'
                            : 'Switch to Textured'}
                    </strong>
                }>
                <StyledImageAction
                    className="icon_button"
                    style={{
                        backgroundColor: 'transparent',
                    }}
                    $expanded={expanded}
                    src={
                        getColoredImage(
                            showTexture ? Wireframe : Solid,
                            '#204480',
                            primaryColour
                        ) as string
                    }
                    onClick={togglePreviewWithSurvey}
                />
            </OverlayTrigger>

            {!hideExpandIcon && !isMiniBrowser ? (
                !expanded ? (
                    <>
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <strong>Expand Product Visualiser</strong>
                            }>
                            <StyledImageAction
                                className="icon_button"
                                style={{
                                    backgroundColor: 'transparent',
                                }}
                                src={
                                    getColoredImage(
                                        Expand,
                                        '#204380',
                                        primaryColour
                                    ) as string
                                }
                                onClick={expandedViewHandler}
                            />
                        </OverlayTrigger>
                        <ExpandedView
                            previewLocation={previewLocation}
                            open={showExpandedView[`${previewLocation}`]}
                            setShowExpandedView={setShowExpandedViewCallback}
                        />
                    </>
                ) : (
                    <OverlayTrigger
                        placement="bottom"
                        overlay={<strong>Close Product Visualiser</strong>}>
                        <StyledImageAction
                            className="icon_button"
                            style={{
                                backgroundColor: 'transparent',
                            }}
                            src={
                                getColoredImage(
                                    Collapse,
                                    '#005b7f',
                                    primaryColour
                                ) as string
                            }
                            onClick={expandedViewHandler}
                        />
                    </OverlayTrigger>
                )
            ) : null}

            {hasDivider ? <Divider /> : null}

            {options.map((option) => (
                <OverlayTrigger
                    key={option.title}
                    placement="bottom"
                    overlay={<strong>{option.title}</strong>}>
                    <StyledImage
                        className="icon_button"
                        $selected={
                            option.selected && isPreviewHighlighted
                                ? true
                                : false
                        }
                        $expanded={expanded}
                        onClick={option.clickHandler}
                        src={
                            getColoredImage(
                                option.imgSrc,
                                option.defaultColor,
                                secondaryColour
                            ) as string
                        }
                    />
                </OverlayTrigger>
            ))}
        </ViewOptionContainer>
    );
};

const ViewOptionContainer = styled.div<ViewOptionProps>`
    display: flex;
    align-items: center;
    justify-content: ${({direction = 'vertical'}) =>
        direction === 'horizontal' ? 'space-evenly' : 'center'};
    width: ${({direction = 'vertical'}) =>
        direction === 'horizontal' ? '100%' : '50px'};
    min-width: 50px;
    border-radius: ${({direction = 'vertical'}) =>
        direction === 'horizontal' ? '8px 8px 0 0' : '0 8px 8px 0'};
    background: #eee;
    flex-direction: column;
    height: 100%;
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */

    ${({$isMiniBrowser}) => {
        if ($isMiniBrowser) {
            return `
                gap: 5px;

                > img {
                    max-height: 28px;
                    margin: 0;
                    border: 0;
                }
            `;
        }
    }}
    flex-direction: ${({direction = 'vertical'}) =>
        direction === 'horizontal' ? 'row' : 'column'};
    height: ${({direction = 'vertical'}) =>
        direction === 'horizontal' ? '50px' : '100%'};
    position: ${({direction = 'vertical'}) =>
        direction === 'horizontal' ? 'fixed' : 'relative'};
    bottom: ${({direction = 'horizontal'}) => (direction ? '0' : 'auto')};
    left: ${({direction = 'horizontal'}) => (direction ? '0' : 'auto')};
    user-select: none;
    padding: 5px;
`;

const StyledImage = styled(Image)<{$selected?: boolean; $expanded?: boolean}>`
    flex: 1;
    fill: red;
    max-height: ${({$expanded}) => ($expanded ? '45px' : '34px')};
    cursor: pointer;
    margin: 5px;
    border: 2px solid transparent;
    border-radius: 4px;
    ${({$selected}) =>
        $selected ? 'border-color: rgb(var(--secondary_colour));' : ''}
`;

const StyledImageAction = styled(Image)<{$expanded?: boolean}>`
    flex: 1;
    max-height: ${({$expanded}) => ($expanded ? '50px' : '36px')};
    cursor: pointer;
    border: 2px solid transparent;
    margin: 2px;
`;

const Divider = styled.div`
    width: 100%;
    height: 2px;
    margin: 7px 5px;
    background-color: #bdbdbd;
`;

export default PreviewControl;
